/* ActivityDetail.jsx - Detailseite für eine Aktivität
   - Zeigt alle Informationen zu einer Aktivität an
   - Integriert EventScheduleDisplay für Zeitangaben
*/
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { activitiesAPI } from '../services/api';
import { trackEvent, setCurrentActivity } from '../services/analytics';
import { FilterContext } from '../context/FilterContext';
import Loading from '../components/Common/Loading';
import EventScheduleDisplay from '../components/Common/EventScheduleDisplay';
import { DescOutput } from '../components/Common/Dialog';

import { MapsButton, SBBButton } from '../components/Common/ReferenceButtons';
import ActivityWheelchairIndicator from '../components/Common/ActivityWheelchairIndicator';
import {OeVConnectionDisplay } from '../components/Common/OeVConnectionDisplay';
import { LocationBox } from '../components/Common/Card';
import { ActivityTopSection, ActivityCategoryList } from '../components/Grid/ActivityCommon';
import { AddressField } from './PagesCommon';

const ActivityDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const filterDate = searchParams.get('date'); // Lese Filterdatum aus URL
  const activeTime = searchParams.get('time'); // Lese aktive Zeit aus URL (von Card)
  const { filters } = useContext(FilterContext) || {};
  const [activity, setActivity] = useState(null);
  const [travelPlan, setTravelPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [travelLoading, setTravelLoading] = useState(false);
  const [error, setError] = useState(null);
  const [travelError, setTravelError] = useState(null);
  const isCoordinateOnlyLocation = activity?.address?.startsWith('Koordinaten:');

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const response = await activitiesAPI.getById(id);
        setActivity(response.data);
        setCurrentActivity(response.data.id, response.data.event_type);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [id]);

  // Umami: einmal pro Aktivitäts-ID tracken (auch unter StrictMode/Re-Renders)
  const trackedIdRef = useRef(null);
  const activityId = activity?.id;
  useEffect(() => {
    if (activityId == null || trackedIdRef.current === activityId) {
      return;
    }
    trackedIdRef.current = activityId;
    trackEvent('activity-view', {
      id: activityId,
      event_type: activity.event_type,
      name: (activity.name || '').slice(0, 50),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId]);

  useEffect(() => {
    if (!activity) {
      return undefined;
    }

    // Die Detailansicht nutzt OJP (detail=true), nicht die SBB-API der Cards.
    // Sie läuft deshalb nicht über die serielle Card-Warteschlange – sonst
    // wartet sie auf eine gerade laufende Card-Abfrage, die bei gedrosselter
    // SBB-API lange hängen kann.
    const controller = new AbortController();
    let cancelled = false;

    const fetchTravelPlan = async () => {
      setTravelLoading(true);
      setTravelError(null);

      try {
        const travelParams = {
          from_address: filters.start_address,
          departure_date: filters.departure_date,
          departure_time: filters.departure_time,
          detail: true,
        };
        if (activeTime) {
          travelParams.event_start_time = activeTime;
        }
        const response = await activitiesAPI.getTravelPlan(id, travelParams, {
          signal: controller.signal,
        });
        if (cancelled) return;
        if (response.data?.no_connection) {
          setTravelError('Keine ÖV-Verbindung gefunden');
        } else if (response.data?.unavailable) {
          setTravelError('ÖV-Auskunft vorübergehend nicht erreichbar – bitte später erneut versuchen');
        } else {
          setTravelPlan(response.data);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching travel plan:', err);
        setTravelError('Keine Verbindung gefunden');
      } finally {
        if (!cancelled) setTravelLoading(false);
      }
    };

    fetchTravelPlan();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [activity, id, filters.start_address, filters.departure_date, filters.departure_time, activeTime]);

  if (loading) return <Loading />;
  if (error) return <div>Error: {error}</div>;
  if (!activity) return <div>Activity not found</div>;

  const openGoogleMapsRoute = (startAddress, destination) => {
    const origin = encodeURIComponent(startAddress);
    const dest = encodeURIComponent(destination);
    
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&travelmode=transit`;
  };

  const activityDestination = activity.location_source === 'address'
    ? activity.address
    : activity.latitude && activity.longitude
      ? `${activity.latitude},${activity.longitude}`
      : activity.address;
  const fromAddress = travelPlan?.from_address || filters?.start_address;
  const mapsHref = fromAddress
    ? openGoogleMapsRoute(fromAddress, activityDestination)
    : activity.location_source === 'address'
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activity.address)}`
      : activity.latitude && activity.longitude
        ? `https://www.google.com/maps/search/?api=1&query=${activity.latitude},${activity.longitude}`
        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(activity.address)}`;

  // Startadresse = Zieladresse: es gibt keine Verbindung zu zeigen
  const isAtDestination = travelPlan?.at_destination === true;
  // Reiner Fussweg: keine Haltestellen, also kein SBB-Fahrplan-Link
  const isWalkOnly = travelPlan?.walk_only === true;

  const sbbHref = travelPlan && !travelPlan.no_connection && !isAtDestination && !isWalkOnly
    ? `https://www.sbb.ch/de/kaufen/pages/fahrplan/fahrplan.xhtml?von=${encodeURIComponent(travelPlan.from_address || travelPlan.from_station)}&nach=${encodeURIComponent(travelPlan.to_station)}&datum=${new Date(travelPlan.departure).toLocaleDateString('de-CH')}&zeit=${new Date(travelPlan.departure).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}`
    : null;

  // Umami: Klicks auf die externen Links (nur hier, nicht auf der Kontrollseite)
  const trackRouteView = () => trackEvent('route-view', { activity_id: activity.id });
  const trackGoogleView = () => trackEvent('google-view', { activity_id: activity.id });

  return (
    <>
      <ActivityWheelchairIndicator wheelchairAccessible={activity.wheelchair_accessible} />
      <div className="activity-header">
        <div className="activity-header-content">
          <ActivityTopSection activity={activity} />
          <div className="activity-detail-header">
            <h2 className="headings Font-H0-A-120-T Colors-Black">{activity.name}</h2>            
            <DescOutput description={activity.description} />
          </div>
        </div>
        <ActivityCategoryList
          categories={activity.categories}
          className="activity-detail-categories"
          chipClassName="tag tag--category"
        />
      </div>
      <div className="activity-detail">
      {/* 2-Spalten-Layout (Desktop) */}
      <div className="activity-detail-columns">

        {/* Linke Spalte: Ortsbezeichnung, Termine */}
        

        <div className="activity-detail-col">

          <LocationBox locationName={activity.location_name} />
          
          <div className="activity-detail-left-col">
            {/* Zeitangaben */}
            <EventScheduleDisplay
              eventType={activity.event_type}
              openingHours={activity.opening_hours}
              eventSchedule={activity.event_schedule}
              accessibleFrom={activity.accessible_from}
              accessibleUntil={activity.accessible_until}
              website={activity.website}
              filterDate={filterDate}
              activeTimeStart={activeTime}
              onWebsiteClick={() => trackEvent('website-click', {
                source: 'event_website',
                activity_id: activity.id,
              })}
            />
          </div>
        </div>

        {/* Rechte Spalte: Adresse, Karte, SBB, ÖV */}
        <div className="activity-detail-col">
          {/* Adresse + Desktop-Buttons */}
          <div className="activity-detail-address-section">
          
            <div className="anreise-header">          
              <AddressField
                address={activity.address}
                showTravelTime
                travelLoading={travelLoading}
                travelError={travelError}
                travelTime={travelPlan?.duration_minutes ?? null}
                atDestination={isAtDestination}
                headerActions={
                  <div className="activity-detail-address-actions activity-detail-actions-desktop">
                    <SBBButton href={sbbHref} onTrack={trackRouteView} />
                    <MapsButton href={mapsHref} onTrack={trackGoogleView} />
                  </div>
                }
              />

              {/* ÖV-Fahrempfehlung */}
              <OeVConnectionDisplay
                travelLoading={travelLoading}
                travelError={travelError}
                travelPlan={travelPlan}
                isCoordinateOnlyLocation={isCoordinateOnlyLocation}
                sbbHref={sbbHref}
              />   
              {/* Mobile/Tablet: Buttons am Ende */}
              <div className="activity-detail-actions-mobile">
                <SBBButton href={sbbHref} onTrack={trackRouteView} />
                <MapsButton href={mapsHref} onTrack={trackGoogleView} />
              </div>
            </div>   
          </div>  
        </div>
      </div>
    </div>
    </>
  );
};

export default ActivityDetail;
